import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, DatePicker, Tag, message, Divider, Row, Col, Card, Checkbox, Alert, Switch } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, ClearOutlined, FilterOutlined, UserAddOutlined } from '@ant-design/icons';
import { alunoService, unidadeService, serieService, responsavelService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';

const AlunosList = () => {
  const { user } = useAuth();
  const [alunos, setAlunos] = useState([]);
  const [alunosFiltrados, setAlunosFiltrados] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isResponsavelModalVisible, setIsResponsavelModalVisible] = useState(false);
  const [loadingResponsavel, setLoadingResponsavel] = useState(false);
  const [alunoResponsavelAtual, setAlunoResponsavelAtual] = useState(null);
  const [responsaveisAlunoAtual, setResponsaveisAlunoAtual] = useState([]);
  const [responsavelEmEdicao, setResponsavelEmEdicao] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [form] = Form.useForm();
  const [responsavelForm] = Form.useForm();
  const LEGACY_RESPONSAVEL_FIELDS = [
    'nome_mae',
    'cpf_mae',
    'tel_mae',
    'email_mae',
    'mae_resp_financeiro',
    'nome_pai',
    'cpf_pai',
    'tel_pai',
    'email_pai',
    'pai_resp_financeiro',
  ];
  const LEGACY_ENDERECO_FIELDS = [
    'endereco',
    'numero',
    'complemento',
    'bairro',
    'cep',
  ];
  const [filtros, setFiltros] = useState({
    busca_geral: '',
    id_unidades: null,
    status: null,
  });
  const [filtrosAplicados, setFiltrosAplicados] = useState({
    busca_geral: '',
    id_unidades: null,
    status: null,
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [sorting, setSorting] = useState({ sortBy: null, sortOrder: null });

  const getApiErrorMessage = (error, fallbackMessage) => {
    const detail = error?.response?.data?.detail;

    if (Array.isArray(detail)) {
      const mensagens = detail
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item?.msg) return item.msg;
          return null;
        })
        .filter(Boolean);

      if (mensagens.length) {
        return mensagens.join(' | ');
      }
    }

    if (detail && typeof detail === 'object') {
      if (detail.msg) return detail.msg;
      try {
        return JSON.stringify(detail);
      } catch (_e) {
        return fallbackMessage;
      }
    }

    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }

    return fallbackMessage;
  };

  useEffect(() => {
    fetchUnidades();
    fetchSeries();
  }, []);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const aplicarFiltros = () => {
    setFiltrosAplicados({ ...filtros });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const limparFiltros = () => {
    const reset = { busca_geral: '', id_unidades: null, status: null };
    setFiltros(reset);
    setFiltrosAplicados(reset);
    setPagination((prev) => ({ ...prev, current: 1 }));
    message.success('Filtros limpos');
  };

  const fetchAlunos = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
      };
      if (sorting.sortBy && sorting.sortOrder) {
        params.sort_by = sorting.sortBy;
        params.sort_order = sorting.sortOrder;
      }
      if (filtrosAplicados.busca_geral?.trim()) params.busca_geral = filtrosAplicados.busca_geral.trim();
      if (filtrosAplicados.id_unidades) params.id_unidades = filtrosAplicados.id_unidades;
      if (filtrosAplicados.status) params.status = filtrosAplicados.status;

      const response = await alunoService.list(params);
      const payload = response?.data || {};
      const items = Array.isArray(payload?.items)
        ? payload.items
        : (Array.isArray(payload) ? payload : []);
      const apiPagination = payload?.pagination || {};

      setAlunos(items);
      setAlunosFiltrados(items);
      setPagination((prev) => ({
        ...prev,
        current: apiPagination.page || prev.current,
        pageSize: apiPagination.page_size || prev.pageSize,
        total: typeof apiPagination.total === 'number' ? apiPagination.total : items.length,
      }));
    } catch (error) {
      message.error('Erro ao carregar alunos');
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAlunos();
  }, [pagination.current, pagination.pageSize, filtrosAplicados, sorting.sortBy, sorting.sortOrder]);

  const onTableChange = (newPagination, _filters, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination?.current || 1,
      pageSize: newPagination?.pageSize || prev.pageSize,
    }));

    const sorterObj = Array.isArray(sorter) ? sorter[0] : sorter;
    const fieldMap = {
      id: 'id',
      nome_aluno: 'nome_aluno',
      status_aluno: 'status',
    };

    if (sorterObj?.order) {
      const sortBy = fieldMap[sorterObj.field || sorterObj.columnKey];
      if (sortBy) {
        setSorting({
          sortBy,
          sortOrder: sorterObj.order === 'ascend' ? 'asc' : 'desc',
        });
      }
      return;
    }

    if (sorterObj?.field || sorterObj?.columnKey) {
      setSorting({ sortBy: null, sortOrder: null });
    }
  };

  const fetchUnidades = async () => {
    try {
      const response = await unidadeService.list();
      setUnidades(response.data);
    } catch (error) {
      console.error('Erro ao carregar unidades');
    }
  };

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    const formData = {
      ...record,
      data_nascimento: record.data_nascimento ? dayjs(record.data_nascimento) : null,
    };
    form.setFieldsValue(formData);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Confirmação',
      content: 'Deseja excluir este aluno?',
      okText: 'Sim',
      cancelText: 'Não',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await alunoService.delete(id);
          message.success('Aluno excluído com sucesso');
          fetchAlunos();
        } catch (error) {
          message.error('Erro ao excluir aluno');
        }
      },
    });
  };

  const carregarResponsaveisDoAluno = async (aluno) => {
    setLoadingResponsavel(true);
    try {
      const response = await responsavelService.listByAluno(aluno.id);
      const vinculados = Array.isArray(response.data) ? response.data : [];
      setResponsaveisAlunoAtual(vinculados);
    } catch (error) {
      message.error('Erro ao carregar responsáveis do aluno');
      console.error(error);
    } finally {
      setLoadingResponsavel(false);
    }
  };

  const abrirModalResponsaveis = async (aluno) => {
    setAlunoResponsavelAtual(aluno);
    setResponsavelEmEdicao(null);
    responsavelForm.resetFields();
    setIsResponsavelModalVisible(true);
    await carregarResponsaveisDoAluno(aluno);
  };

  const handleEditarResponsavelAluno = (responsavel) => {
    if (!alunoResponsavelAtual) return;

    setResponsavelEmEdicao(responsavel);
    responsavelForm.setFieldsValue({
      nome_completo: responsavel.nome_completo || '',
      cpf: (responsavel.cpf || '').replace(/\D/g, ''),
      email: responsavel.email || '',
      telefone: responsavel.telefone || '',
      endereco: responsavel.endereco || '',
      numero: responsavel.numero || '',
      complemento: responsavel.complemento || '',
      bairro: responsavel.bairro || '',
      cep: responsavel.cep || '',
      tipo_vinculo: responsavel.vinculo_aluno?.tipo_vinculo || 'RESPONSAVEL',
      is_responsavel_financeiro: !!responsavel.vinculo_aluno?.is_responsavel_financeiro,
    });
  };

  const limparEdicaoResponsavel = () => {
    setResponsavelEmEdicao(null);
    responsavelForm.resetFields();
  };

  const handleCriarResponsavelAluno = async () => {
    if (!alunoResponsavelAtual) return;

    if (!responsavelEmEdicao && responsaveisAlunoAtual.length >= 2) {
      message.warning('Este aluno já possui 2 responsáveis vinculados.');
      return;
    }

    try {
      const values = await responsavelForm.validateFields();
      const payload = {
        nome_completo: values.nome_completo,
        cpf: values.cpf.replace(/\D/g, ''),
        email: values.email,
        telefone: values.telefone || null,
        endereco: values.endereco || null,
        numero: values.numero || null,
        complemento: values.complemento || null,
        bairro: values.bairro || null,
        cep: (values.cep || '').replace(/\D/g, '') || null,
        id_aluno: alunoResponsavelAtual.id,
        tipo_vinculo: values.tipo_vinculo,
        is_responsavel_financeiro: !!values.is_responsavel_financeiro,
      };

      if (responsavelEmEdicao) {
        await responsavelService.update(responsavelEmEdicao.id, payload);
        message.success('Responsável atualizado com sucesso');
      } else {
        await responsavelService.create(payload);
        message.success('Responsável cadastrado e vinculado com sucesso');
      }
      limparEdicaoResponsavel();
      await carregarResponsaveisDoAluno(alunoResponsavelAtual);
      await fetchAlunos();
    } catch (error) {
      message.error(getApiErrorMessage(error, responsavelEmEdicao ? 'Erro ao atualizar responsável' : 'Erro ao cadastrar responsável'));
      console.error(error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const submitData = {
        ...values,
        data_nascimento: values.data_nascimento ? values.data_nascimento.format('YYYY-MM-DD') : null,
      };

      LEGACY_RESPONSAVEL_FIELDS.forEach((field) => {
        delete submitData[field];
      });

      LEGACY_ENDERECO_FIELDS.forEach((field) => {
        delete submitData[field];
      });

      if (editingId) {
        await alunoService.update(editingId, submitData);
        message.success('Aluno atualizado com sucesso');
      } else {
        await alunoService.create(submitData);
        message.success('Aluno criado com sucesso');
      }
      setIsModalVisible(false);
      fetchAlunos();
    } catch (error) {
      message.error('Erro ao salvar aluno');
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      ATIVO: 'green',
      INATIVO: 'red',
      TRANSFERIDO: 'orange',
      CONCLUIDO: 'blue',
    };
    return colors[status] || 'default';
  };

  const getUnidadeName = (id_unidades) => {
    const unidade = unidades.find((u) => u.id === id_unidades);
    return unidade ? unidade.nome_fantasia : '-';
  };

  const getSerieName = (id_serie) => {
    const serie = series.find((s) => s.id === id_serie);
    return serie ? serie.nome_serie : '-';
  };
  const fetchSeries = async () => {
    try {
      const response = await serieService.list();
      setSeries(response.data);
    } catch (error) {
      console.error('Erro ao carregar séries');
    }
  };

  const getSeriesPorUnidade = (id_unidades) => {
    if (!id_unidades) return [];
    return series.filter((s) => s.id_unidades === id_unidades);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      sorter: true,
      sortOrder: sorting.sortBy === 'id'
        ? (sorting.sortOrder === 'asc' ? 'ascend' : 'descend')
        : null,
    },
    {
      title: 'Nome do Aluno',
      dataIndex: 'nome_aluno',
      key: 'nome_aluno',
      sorter: true,
      sortOrder: sorting.sortBy === 'nome_aluno'
        ? (sorting.sortOrder === 'asc' ? 'ascend' : 'descend')
        : null,
    },
    {
      title: 'Série',
      dataIndex: 'id_serie',
      key: 'id_serie',
      render: (id_serie) => getSerieName(id_serie),
    },
    {
      title: 'Resp. Financeiro',
      key: 'responsavel_financeiro',
      render: (_, record) => record.responsavel_financeiro_nome || '-',
      width: 160,
    },
    {
      title: 'Unidade',
      dataIndex: 'id_unidades',
      key: 'id_unidades',
      render: (id_unidades) => getUnidadeName(id_unidades),
    },
    {
      title: 'Status',
      dataIndex: 'status_aluno',
      key: 'status_aluno',
      render: (text) => (
        <Tag color={getStatusColor(text)}>{text}</Tag>
      ),
      sorter: true,
      sortOrder: sorting.sortBy === 'status'
        ? (sorting.sortOrder === 'asc' ? 'ascend' : 'descend')
        : null,
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button
            icon={<UserAddOutlined />}
            onClick={() => abrirModalResponsaveis(record)}
            size="small"
            title="Cadastrar responsáveis"
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          {user?.role !== 'OFFICE' && (
            <Button
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleDelete(record.id)}
              size="small"
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {user?.role === 'RESPONSAVEL' && !loading && alunos.length === 0 && (
        <Alert
          message="Nenhum aluno vinculado à sua conta no momento."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {/* Painel de Filtros */}
      <Card 
        title={
          <Space>
            <FilterOutlined />
            Filtros de Busca
            <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#888' }}>
              ({alunos.length} de {pagination.total} alunos)
            </span>
          </Space>
        }
        extra={
          <Button type="text" size="small" onClick={() => setMostrarFiltros(!mostrarFiltros)}>
            {mostrarFiltros ? 'Ocultar' : 'Mostrar'}
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {mostrarFiltros && (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Busca Geral</div>
              <Input
                placeholder="Pesquisar por nome, CPF..."
                prefix={<SearchOutlined />}
                value={filtros.busca_geral}
                onChange={(e) => handleFiltroChange('busca_geral', e.target.value)}
                onPressEnter={aplicarFiltros}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Unidade</div>
              <Select
                placeholder="Todas as unidades"
                value={filtros.id_unidades}
                onChange={(value) => handleFiltroChange('id_unidades', value)}
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
              >
                {unidades.map(u => (
                  <Select.Option key={u.id} value={u.id}>{u.nome_fantasia}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Status</div>
              <Select
                placeholder="Todos os status"
                value={filtros.status}
                onChange={(value) => handleFiltroChange('status', value)}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="ATIVO">Ativo</Select.Option>
                <Select.Option value="INATIVO">Inativo</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>&nbsp;</div>
              <Button icon={<SearchOutlined />} type="primary" onClick={aplicarFiltros} block style={{ marginBottom: 8 }}>
                Buscar
              </Button>
              <Button icon={<ClearOutlined />} onClick={limparFiltros} block>
                Limpar Filtros
              </Button>
            </Col>
          </Row>
        )}
      </Card>

      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Novo Aluno
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={alunosFiltrados}
        loading={loading}
        rowKey="id"
        pagination={{ 
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total de ${total} alunos`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        onChange={onTableChange}
        scroll={{ x: 1400, y: 520 }}
        sticky
      />

      <Modal
        title={editingId ? 'Editar Aluno' : 'Novo Aluno'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        width={800}
        zIndex={1000}
        getContainer={() => document.body}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* Informações Básicas */}
          <Divider>Informações Básicas</Divider>

          <Form.Item
            name="nome_aluno"
            label="Nome do Aluno"
            rules={[{ required: true, message: 'Nome é obrigatório' }]}
          >
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="id_unidades"
                label="Unidade"
                rules={[{ required: true, message: 'Unidade é obrigatória' }]}
              >
                <Select placeholder="Selecione a unidade">
                  {unidades.map((unidade) => (
                    <Select.Option key={unidade.id} value={unidade.id}>
                      {unidade.nome_fantasia}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="data_nascimento" label="Data de Nascimento">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="status_aluno" label="Status" initialValue="ATIVO">
                <Select>
                  <Select.Option value="ATIVO">Ativo</Select.Option>
                  <Select.Option value="INATIVO">Inativo</Select.Option>
                  <Select.Option value="TRANSFERIDO">Transferido</Select.Option>
                  <Select.Option value="CONCLUIDO">Concluído</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.id_unidades !== curr.id_unidades}>
                {({ getFieldValue }) => {
                  const unidadeId = getFieldValue('id_unidades');
                  const seriesFiltradas = getSeriesPorUnidade(unidadeId);
                  return (
                    <Form.Item name="id_serie" label="Série">
                      <Select
                        placeholder={unidadeId ? 'Selecione a série' : 'Selecione a unidade primeiro'}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={!unidadeId}
                        filterOption={(input, option) =>
                          option.children.toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {seriesFiltradas.map((serie) => (
                          <Select.Option key={serie.id} value={serie.id}>
                            {serie.nome_serie}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="autorizacao_imagem" valuePropName="checked">
                <Checkbox>Autorização de imagem</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Cadastro de responsáveis"
            description="Os dados de pai/mãe e responsável financeiro são gerenciados na tela Responsáveis. O cadastro de aluno não aceita mais edição desses campos."
          />

          <Button
            icon={<UserAddOutlined />}
            onClick={() => {
              if (!editingId) {
                message.info('Salve o aluno primeiro para cadastrar os responsáveis vinculados.');
                return;
              }
              const alunoEdicao = alunos.find((a) => a.id === editingId);
              if (!alunoEdicao) {
                message.warning('Aluno não encontrado para vincular responsáveis.');
                return;
              }
              abrirModalResponsaveis(alunoEdicao);
            }}
            style={{ marginBottom: 16 }}
          >
            Cadastrar Responsáveis
          </Button>

          <Divider>Condição Financeira</Divider>

          <Form.Item
            name="bolsista"
            valuePropName="checked"
          >
            <Checkbox>Aluno bolsista (permite matrícula sem valor total)</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={alunoResponsavelAtual ? `Responsáveis de ${alunoResponsavelAtual.nome_aluno}` : 'Responsáveis do aluno'}
        open={isResponsavelModalVisible}
        onCancel={() => {
          setIsResponsavelModalVisible(false);
          setAlunoResponsavelAtual(null);
          setResponsaveisAlunoAtual([]);
          limparEdicaoResponsavel();
        }}
        footer={null}
        width={760}
        zIndex={1100}
        getContainer={() => document.body}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Limite operacional: até 2 responsáveis por aluno (${responsaveisAlunoAtual.length}/2).`}
        />

        <Table
          size="small"
          loading={loadingResponsavel}
          rowKey="id"
          dataSource={responsaveisAlunoAtual}
          pagination={false}
          style={{ marginBottom: 16 }}
          locale={{ emptyText: 'Nenhum responsável vinculado ainda' }}
          columns={[
            { title: 'Nome', dataIndex: 'nome_completo', key: 'nome_completo' },
            { title: 'CPF', dataIndex: 'cpf', key: 'cpf' },
            { title: 'Email', dataIndex: 'email', key: 'email' },
            {
              title: 'Tipo',
              key: 'tipo_vinculo',
              render: (_, record) => record.vinculo_aluno?.tipo_vinculo || '-',
              width: 120,
            },
            {
              title: 'Financeiro',
              key: 'is_responsavel_financeiro',
              render: (_, record) => (record.vinculo_aluno?.is_responsavel_financeiro ? 'Sim' : 'Não'),
              width: 110,
            },
            {
              title: 'Ações',
              key: 'acoes',
              width: 90,
              render: (_, record) => (
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => handleEditarResponsavelAluno(record)}
                  title="Editar responsável"
                />
              ),
            },
          ]}
        />

        <Divider style={{ marginTop: 8 }}>
          {responsavelEmEdicao ? `Editar responsável: ${responsavelEmEdicao.nome_completo}` : 'Novo responsável para este aluno'}
        </Divider>

        <Form form={responsavelForm} layout="vertical" onFinish={handleCriarResponsavelAluno}>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="nome_completo"
                label="Nome Completo"
                rules={[{ required: true, message: 'Nome é obrigatório' }]}
              >
                <Input placeholder="Nome do responsável" disabled={responsaveisAlunoAtual.length >= 2} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="cpf"
                label="CPF"
                rules={[{ required: true, message: 'CPF é obrigatório' }]}
                getValueFromEvent={(e) => e.target.value.replace(/\D/g, '').slice(0, 11)}
              >
                <Input placeholder="00000000000" maxLength={11} disabled={!responsavelEmEdicao && responsaveisAlunoAtual.length >= 2} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Email é obrigatório' },
                  { type: 'email', message: 'Email inválido' },
                ]}
              >
                <Input placeholder="email@dominio.com" disabled={!responsavelEmEdicao && responsaveisAlunoAtual.length >= 2} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="telefone" label="Telefone">
                <Input placeholder="(11) 99999-9999" disabled={!responsavelEmEdicao && responsaveisAlunoAtual.length >= 2} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="endereco" label="Endereço">
                <Input placeholder="Rua, avenida..." disabled={!responsavelEmEdicao && responsaveisAlunoAtual.length >= 2} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="numero" label="Número">
                <Input placeholder="123" disabled={!responsavelEmEdicao && responsaveisAlunoAtual.length >= 2} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="complemento" label="Complemento">
                <Input placeholder="Apto, bloco..." disabled={!responsavelEmEdicao && responsaveisAlunoAtual.length >= 2} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="bairro" label="Bairro">
                <Input placeholder="Bairro" disabled={!responsavelEmEdicao && responsaveisAlunoAtual.length >= 2} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="cep"
                label="CEP"
                getValueFromEvent={(e) => e.target.value.replace(/\D/g, '').slice(0, 8)}
              >
                <Input placeholder="00000000" maxLength={8} disabled={!responsavelEmEdicao && responsaveisAlunoAtual.length >= 2} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="tipo_vinculo"
                label="Tipo de Responsável"
                initialValue="RESPONSAVEL"
                rules={[{ required: true, message: 'Selecione o tipo' }]}
              >
                <Select disabled={!responsavelEmEdicao && responsaveisAlunoAtual.length >= 2}>
                  <Select.Option value="MAE">Mãe</Select.Option>
                  <Select.Option value="PAI">Pai</Select.Option>
                  <Select.Option value="OUTRO">Outro</Select.Option>
                  <Select.Option value="RESPONSAVEL">Responsável</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="is_responsavel_financeiro"
                label="Responsável Financeiro"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch disabled={!responsavelEmEdicao && responsaveisAlunoAtual.length >= 2} checkedChildren="Sim" unCheckedChildren="Não" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {responsavelEmEdicao && (
              <Button
                style={{ marginRight: 8 }}
                onClick={limparEdicaoResponsavel}
              >
                Cancelar edição
              </Button>
            )}
            <Button
              type="primary"
              onClick={() => responsavelForm.submit()}
              disabled={!responsavelEmEdicao && responsaveisAlunoAtual.length >= 2}
              loading={loadingResponsavel}
            >
              {responsavelEmEdicao ? 'Salvar edição' : 'Cadastrar responsável'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AlunosList;
